export const initialChatState = { messages: [], status: 'idle', activity: [], waiting: false };
function patchLast(state, patch) {
    if (state.messages.length === 0)
        return state;
    const last = state.messages[state.messages.length - 1];
    return { ...state, messages: [...state.messages.slice(0, -1), patch(last)] };
}
function fail(state, message) {
    return {
        ...patchLast(state, (m) => ({ ...m, partial: true, error: message })),
        status: 'error',
        error: message,
        activity: [],
        waiting: false,
    };
}
function applyEvent(state, event) {
    switch (event.type) {
        case 'text':
            return patchLast({ ...state, waiting: false }, (m) => ({ ...m, content: m.content + event.delta }));
        case 'tool_start':
            return { ...state, activity: [...state.activity, `${event.name} ${event.summary}`.trim()] };
        case 'tool_end': {
            const index = state.activity.findIndex((line) => line.startsWith(event.name));
            return index < 0 ? state : { ...state, activity: state.activity.filter((_, i) => i !== index), waiting: true };
        }
        case 'citations':
            return patchLast(state, (m) => ({ ...m, citations: event.items }));
        case 'suggestions':
            return patchLast(state, (m) => ({ ...m, suggestions: event.items }));
        case 'done':
            return patchLast(state, (m) => ({
                ...m,
                partial: event.partial,
                ...(event.partialReason && { partialReason: event.partialReason }),
            }));
        case 'error':
            return fail(state, event.message);
    }
}
export function chatReducer(state, action) {
    switch (action.type) {
        case 'submit':
            return {
                messages: [
                    ...state.messages,
                    { role: 'user', content: action.text },
                    { role: 'assistant', content: '', partial: true },
                ],
                status: 'streaming',
                error: undefined,
                activity: [],
                waiting: true,
            };
        case 'event':
            return applyEvent(state, action.event);
        case 'fail':
            return fail(state, action.message);
        case 'complete':
            return { ...state, status: 'idle', activity: [], waiting: false };
        case 'reset':
            return initialChatState;
    }
}
/** The label under a partial answer: the turn's error, else the server's reason, else the generic label (older server). */
export function partialLabel(m) {
    if (m.error)
        return `Partial answer: ${m.error}`;
    switch (m.partialReason?.kind) {
        case 'round_cap':
            return 'Partial answer: tool round limit reached';
        case 'blank':
            return 'Partial answer: the model returned no text';
        case 'stop_reason':
            return `Partial answer: the model stopped early (${m.partialReason.finishReason})`;
        case undefined:
            return 'Partial answer';
    }
}
const THINKING_LINE = 'Thinking…';
// One shared array, so a render-to-render comparison (the panel's scroll effect) sees no change.
const THINKING_ACTIVITY = [THINKING_LINE];
/** The activity lines to show: a "Thinking…" line fills each gap in which the model works with no tool running and no text arriving. */
export function visibleActivity(state) {
    const thinking = state.status === 'streaming' && state.activity.length === 0 && state.waiting;
    return thinking ? THINKING_ACTIVITY : state.activity;
}
export function historyOf(state) {
    return state.messages.filter((m) => m.content.length > 0).map(({ role, content }) => ({ role, content }));
}
export const REQUEST_MAX_MESSAGES = 40;
export const REQUEST_MAX_CONTENT = 8000;
/** The history the client sends: within the server's limits, and starting with a user message. */
export function requestHistoryOf(messages) {
    const clamped = messages.map((m) => ({ ...m, content: m.content.slice(0, REQUEST_MAX_CONTENT) }));
    const windowed = clamped.slice(-REQUEST_MAX_MESSAGES);
    const firstUserIndex = windowed.findIndex((m) => m.role === 'user');
    return firstUserIndex < 0 ? [] : windowed.slice(firstUserIndex);
}
//# sourceMappingURL=chatReducer.js.map